import { useCallback } from 'react';

import { uploadFile, uploadVideo, isVideoFile } from '@ergeon/file-storage';

import { LEVEL } from '../../../../../../envs';
import type { Attachment } from '../types';

interface UseFileUploadPasteOptions {
  attachmentsCount: number;
  maxAttachments: number;
  onAttachmentsAdded: (newAttachments: Attachment[]) => void;
}

/**
 * Shared hook for handling file paste in RichTextInput.
 * Uploads files to S3 and returns markdown references to insert in the editor.
 */
export const useFileUploadPaste = ({
  attachmentsCount,
  maxAttachments,
  onAttachmentsAdded,
}: UseFileUploadPasteOptions) => {
  const handleFilePaste = useCallback(async(files: File[]): Promise<string | null> => {
    const availableSlots = maxAttachments - attachmentsCount;
    if (availableSlots <= 0) return null;

    const filesToProcess = files.slice(0, availableSlots);
    const environment = LEVEL === 'production' ? 'production' : 'development';
    const markdownRefs: string[] = [];
    const newAttachments: Attachment[] = [];

    for (const file of filesToProcess) {
      try {
        let publicUrl: string | void;
        if (isVideoFile(file)) {
          publicUrl = await uploadVideo(file, environment);
        } else {
          publicUrl = await uploadFile(file, undefined, environment);
        }

        if (publicUrl) {
          newAttachments.push({ file, dataURL: publicUrl });
          const altText = isVideoFile(file) ? 'video' : 'image';
          markdownRefs.push(`![${altText}](${publicUrl})`);
        }
      } catch {
        // Skip failed uploads
      }
    }

    if (newAttachments.length > 0) {
      onAttachmentsAdded(newAttachments);
    }

    return markdownRefs.length > 0 ? markdownRefs.join('\n') : null;
  }, [attachmentsCount, maxAttachments, onAttachmentsAdded]);

  return { handleFilePaste };
};
